import { useEffect, useState } from 'react';
import { trackPurchaseComplete, trackEvent } from '../utils/analytics';

/**
 * Post-checkout banner. Stripe redirects back with ?purchase=success|cancelled
 * (one-time app purchase) or ?sync=success ($5/mo Solidly Sync add-on).
 *
 * The GA purchase event fires once per session (sessionStorage guard) so a
 * refresh of the success URL doesn't double-count revenue, and the query
 * params are stripped so the state doesn't survive bookmarks/shares.
 */
const PurchaseSuccess = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('purchase');
    const sync = params.get('sync');

    let next = null;
    if (purchase === 'success') next = 'purchase-success';
    else if (purchase === 'cancelled') next = 'purchase-cancelled';
    else if (sync === 'success') next = 'sync-success';
    if (!next) return;

    setStatus(next);

    const guardKey = `solidly.tracked.${next}`;
    if (!sessionStorage.getItem(guardKey)) {
      sessionStorage.setItem(guardKey, '1');
      if (next === 'purchase-success') {
        trackPurchaseComplete(25);
      } else if (next === 'sync-success') {
        trackEvent('sync_subscription_started', {
          event_category: 'ecommerce',
          value: 5,
          currency: 'USD',
        });
      } else {
        trackEvent('checkout_cancelled', { event_category: 'engagement' });
      }
    }

    params.delete('purchase');
    params.delete('sync');
    const query = params.toString();
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + (query ? `?${query}` : '')
    );
  }, []);

  if (!status) return null;
  if (status === 'purchase-cancelled') {
    return (
      <div className="purchase-banner purchase-banner--muted" role="status">
        <span>Checkout cancelled — no charge was made. Questions? support@solidly.ai</span>
        <button className="purchase-banner-close" onClick={() => setStatus(null)} aria-label="Dismiss">
          ✕
        </button>
      </div>
    );
  }

  const isSync = status === 'sync-success';
  return (
    <div className="purchase-banner" role="status">
      <div className="purchase-banner-body">
        <strong>{isSync ? 'Solidly Sync is active 🎉' : 'You’re in! 🎉'}</strong>
        <span>
          {isSync
            ? 'Head back to the app — Backup & Sync unlocks within a few minutes in Settings.'
            : 'Check your email for your license key and download link. (Give it a minute, and check spam.)'}
        </span>
      </div>
      <button className="purchase-banner-close" onClick={() => setStatus(null)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
};

export default PurchaseSuccess;
